import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { DeleteConfirmationModal } from "../Modals";
import { Icon, Switch } from "../";
import { useUpdateProduct, useDeleteProduct } from "../../../hooks/useProducts";
import { stringToSlug, convertFileToBase64 } from "@finitless/shared/lib";
import { TaxesStep } from "../Business";

const ProductEditTab = ({
  businessId,
  categoryId,
  selectedProduct,
  hasPosIntegration,
  onProductUpsellingUpdate,
  onTaxUpdate,
  onProductDeleted,
  onToggleProduct,
  isLoadingToggle,
}) => {
  const toast = useToast();
  const updateBasicInfoMutation = useUpdateProduct();
  const updateUpsellingMutation = useUpdateProduct();
  const updateTaxMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(selectedProduct);

  const [formData, setFormData] = useState(() => {
    if (selectedProduct) {
      return {
        name: selectedProduct.name || "",
        description: selectedProduct.description || "",
        price: selectedProduct.price?.toString() || "",
        image: selectedProduct.images || null,
      };
    }
    return {
      name: "",
      description: "",
      price: "",
      image: null,
    };
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!selectedProduct) {
      setCurrentProduct(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        image: null,
      });
      setErrors({});
      return;
    }

    const selectedProductId = selectedProduct.id;

    setCurrentProduct((prev) => {
      const shouldUpdate = !prev || prev.id !== selectedProductId;

      if (shouldUpdate) {
        setFormData({
          name: selectedProduct.name || "",
          description: selectedProduct.description || "",
          price: selectedProduct.price?.toString() || "",
          image: selectedProduct.images || null,
        });
        setErrors({});
        return selectedProduct;
      }

      return prev;
    });
  }, [selectedProduct?.id, selectedProduct]);

  const handleToggleUpselling = async (value) => {
    if (!currentProduct || !categoryId) return;
    try {
      await updateUpsellingMutation.mutateAsync({
        businessId,
        categoryId,
        productId: currentProduct.id,
        upselling: value,
      });
      setCurrentProduct({ ...currentProduct, upselling: value });
      if (onProductUpsellingUpdate) {
        onProductUpsellingUpdate(value);
      }
      handleApiSuccess("Upselling updated successfully", toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Update Upselling",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleTaxAssigned = async (tax) => {
    if (!currentProduct || !categoryId) return;
    try {
      const tax_id = tax ? tax.id : null;
      await updateTaxMutation.mutateAsync({
        businessId,
        categoryId,
        productId: currentProduct.id,
        tax_id,
      });
      setCurrentProduct({ ...currentProduct, tax_id, tax });
      if (onTaxUpdate) {
        onTaxUpdate(tax);
      }
      handleApiSuccess(
        tax ? "Tax assigned successfully" : "Tax unassigned successfully",
        toast,
      );
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Update Tax",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleImageChange = async (file) => {
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 512 * 1024) {
        toast.error("Image size must be less than 512KB");
        return;
      }

      try {
        const base64String = await convertFileToBase64(file);
        setFormData((prev) => ({ ...prev, image: base64String }));
      } catch (error) {
        console.error("Error converting image to base64:", error);
        toast.error("Error processing image");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      const limitedValue = value.slice(0, 15);
      setFormData((prev) => ({ ...prev, price: limitedValue }));
      if (errors.price) {
        setErrors((prev) => ({ ...prev, price: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.price?.trim()) {
      newErrors.price = "Price is required";
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = "Price must be a valid positive number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        name: formData.name?.trim(),
        description: formData.description?.trim() || "",
        price: formData.price?.trim(),
        slug: stringToSlug(formData.name || ""),
      };
      // Only include images if a new image was uploaded (base64)
      if (
        formData.image &&
        typeof formData.image === "string" &&
        formData.image.startsWith("data:image/")
      ) {
        payload.images = formData.image;
      }

      await updateBasicInfoMutation.mutateAsync({
        businessId,
        categoryId: currentProduct.category_id || categoryId,
        productId: currentProduct.id,
        ...payload,
      });
      handleApiSuccess("Product updated successfully", toast);
      setCurrentProduct({ ...currentProduct, ...payload });
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Update Product",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleDelete = () => {
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentProduct || !categoryId) return;
    try {
      await deleteProductMutation.mutateAsync({
        businessId,
        categoryId: currentProduct.category_id || categoryId,
        productId: currentProduct.id,
      });
      handleApiSuccess("Product deleted successfully", toast);
      setIsDeleteOpen(false);
      if (onProductDeleted) {
        onProductDeleted(currentProduct.id);
      }
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Delete Product",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleToggleEnabled = async (value) => {
    if (!currentProduct || !onToggleProduct) return;
    try {
      await onToggleProduct(currentProduct.id, value);
      setCurrentProduct({ ...currentProduct, enabled: value });
    } catch {
      //
    }
  };

  return (
    <div
      className="ordering-catalog-surface space-y-6"
      data-ordering-expression="product"
    >
      {/* Product Basic Information */}
      {!hasPosIntegration && (
        <div className="border-border bg-background rounded-lg border p-6">
          <h3 className="text-foreground mb-4 text-lg font-semibold">
            Basic Information
          </h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Image */}
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                Image
              </label>
              <p className="text-muted-foreground mb-3 text-xs">
                Accepted formats: PNG, JPG, JPEG, WEBP (Max 512KB)
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="relative inline-block h-24 w-24">
                  {formData.image ? (
                    <>
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="border-border h-24 w-24 rounded-lg border-2 object-cover"
                      />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                        <div className="flex items-center space-x-1">
                          <Icon name="Image" className="h-5 w-5 text-white" />
                          <Icon
                            name="MoreVertical"
                            className="h-5 w-5 text-white"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="border-border bg-card pointer-events-none flex h-24 w-24 items-center justify-center rounded-lg border">
                      <Icon
                        name="Image"
                        className="text-muted-foreground h-6 w-6"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e.target.files?.[0])}
                    className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                    disabled={updateBasicInfoMutation.isPending}
                    aria-label="Upload image"
                  />
                </div>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={updateBasicInfoMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
                placeholder="Product name"
                required
              />
              {errors.name && (
                <div className="text-destructive mt-1 text-xs">
                  {errors.name}
                </div>
              )}
            </div>

            {/* Price and Description */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="text-foreground mb-2 block text-sm font-medium">
                  Price <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handlePriceChange}
                  maxLength={15}
                  disabled={updateBasicInfoMutation.isPending}
                  className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
                  placeholder="0"
                  required
                />
                {errors.price && (
                  <div className="text-destructive mt-1 text-xs">
                    {errors.price}
                  </div>
                )}
              </div>

              <div>
                <label className="text-foreground mb-2 block text-sm font-medium">
                  Status
                </label>
                <div className="border-border bg-card flex w-full items-center justify-between rounded-lg border px-4 py-2">
                  <span className="text-foreground text-sm font-medium">
                    {currentProduct?.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    id="product-enabled"
                    checked={!!currentProduct?.enabled}
                    onCheckedChange={handleToggleEnabled}
                    disabled={isLoadingToggle}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                disabled={updateBasicInfoMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full resize-none rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
                placeholder="Product description"
                rows={3}
              />
            </div>

            <div className="border-border flex items-center justify-end gap-3 border-t pt-2">
              <Button
                variant="default"
                type="submit"
                disabled={
                  updateBasicInfoMutation.isPending ||
                  !formData.name.trim() ||
                  !formData.price.trim()
                }
                className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {updateBasicInfoMutation.isPending
                  ? "Updating..."
                  : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Product Settings */}
      <div className="border-border bg-background rounded-lg border p-6">
        <h3 className="text-foreground mb-4 text-lg font-semibold">Settings</h3>
        <div className="space-y-4">
          {/* Upselling */}
          <div className="border-border bg-card flex items-center justify-between rounded-lg border p-4">
            <div className="min-w-0 flex-1">
              <h4 className="text-foreground mb-1 text-sm font-medium">
                Upselling
              </h4>
              <p className="text-muted-foreground text-xs">
                Enable upselling for this product
              </p>
            </div>
            <div className="ml-4 flex items-center gap-3">
              <Switch
                checked={!!currentProduct?.upselling}
                onCheckedChange={handleToggleUpselling}
                disabled={
                  updateUpsellingMutation.isPending || hasPosIntegration
                }
              />
            </div>
          </div>

          {/* Taxes */}
          <div className="border-border bg-card rounded-lg border p-4">
            <h4 className="text-foreground mb-3 text-sm font-medium">
              Tax Assignment
            </h4>
            <TaxesStep
              isUpdatingProducts
              businessId={businessId}
              selectedTaxId={currentProduct?.tax_id ?? currentProduct?.tax?.id}
              onAssigned={handleTaxAssigned}
            />
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      {!hasPosIntegration && (
        <div className="border-destructive/50 rounded-lg border bg-red-950/20 p-6">
          <h3 className="text-foreground mb-2 text-lg font-semibold">
            Danger Zone
          </h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Once you delete a product, there is no going back. Please be
            certain.
          </p>
          <Button
            emphasis="solid"
            intent="danger"
            type="button"
            onClick={handleDelete}
            disabled={
              deleteProductMutation.isPending ||
              updateBasicInfoMutation.isPending
            }
            className="flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="Trash" className="h-4 w-4" />
            {deleteProductMutation.isPending ? "Deleting..." : "Delete Product"}
          </Button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        itemName={currentProduct?.name}
        itemType="Product"
        isLoading={deleteProductMutation.isPending}
      />
    </div>
  );
};

export default ProductEditTab;
